// Firebase Web (FCM) config for the admin panel.
//
// These values are NOT secret — they ship to the browser and identify the Firebase
// web app. Fill them from Firebase Console → Project Settings → "Your apps" (Web app),
// and the VAPID key from Cloud Messaging → "Web Push certificates".
//
// Provide them via CRA env vars in admin-panel/.env (must start with REACT_APP_):
//   REACT_APP_FIREBASE_API_KEY=...
//   REACT_APP_FIREBASE_AUTH_DOMAIN=gamizo-db731.firebaseapp.com
//   REACT_APP_FIREBASE_PROJECT_ID=gamizo-db731
//   REACT_APP_FIREBASE_MESSAGING_SENDER_ID=...
//   REACT_APP_FIREBASE_APP_ID=...
//   REACT_APP_FIREBASE_VAPID_KEY=...
//
// The service worker (public/firebase-messaging-sw.js) needs the SAME config; because
// service workers can't read process.env, those values are passed to it via query string
// (see registerPushToken()).

import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
  type Messaging,
} from 'firebase/messaging';

export const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || '';

/** True only when every required web-push config value is present. */
export function isFirebaseConfigured(): boolean {
  return Boolean(
    firebaseConfig.apiKey &&
      firebaseConfig.projectId &&
      firebaseConfig.messagingSenderId &&
      firebaseConfig.appId &&
      VAPID_KEY
  );
}

let app: FirebaseApp | null = null;
function getFirebaseApp(): FirebaseApp {
  if (!app) {
    app = getApps().length ? getApp() : initializeApp(firebaseConfig);
  }
  return app;
}

let messaging: Messaging | null = null;
async function getMessagingIfSupported(): Promise<Messaging | null> {
  try {
    if (!isFirebaseConfigured()) return null;
    if (!(await isSupported())) return null; // e.g. Safari iOS, no SW support
    if (!messaging) messaging = getMessaging(getFirebaseApp());
    return messaging;
  } catch (e) {
    console.warn('[firebase] messaging not available:', e);
    return null;
  }
}

/**
 * Register the firebase-messaging service worker (so push arrives even when the
 * admin tab is closed) and return its registration. The SW config is passed via
 * query string because service workers can't access process.env.
 */
async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  const params = new URLSearchParams({
    apiKey: firebaseConfig.apiKey,
    authDomain: firebaseConfig.authDomain,
    projectId: firebaseConfig.projectId,
    messagingSenderId: firebaseConfig.messagingSenderId,
    appId: firebaseConfig.appId,
  });
  return navigator.serviceWorker.register(`/firebase-messaging-sw.js?${params.toString()}`);
}

/**
 * Ask for notification permission, get the FCM web-push token, and return it.
 * Returns null if unsupported / denied / not configured. Never throws.
 */
export async function getWebPushToken(): Promise<string | null> {
  try {
    const m = await getMessagingIfSupported();
    if (!m) return null;

    if (typeof Notification === 'undefined') return null;
    let permission = Notification.permission;
    if (permission === 'default') {
      permission = await Notification.requestPermission();
    }
    if (permission !== 'granted') return null;

    const swReg = await registerServiceWorker();
    const token = await getToken(m, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg ?? undefined,
    });
    return token || null;
  } catch (e) {
    console.warn('[firebase] getWebPushToken failed:', e);
    return null;
  }
}

/** Current notification permission, or 'unsupported' on environments without the API. */
export type PushPermission = 'granted' | 'denied' | 'default' | 'unsupported';

/**
 * Read the current notification permission WITHOUT prompting the user.
 * Returns 'unsupported' on browsers lacking the Notification/Service Worker APIs.
 */
export function getNotificationPermission(): PushPermission {
  try {
    if (typeof window === 'undefined') return 'unsupported';
    if (typeof Notification === 'undefined' || !('serviceWorker' in navigator)) {
      return 'unsupported';
    }
    return Notification.permission as PushPermission;
  } catch {
    return 'unsupported';
  }
}

/**
 * Fetch the FCM web-push token ONLY if permission is already granted — never prompts.
 * Use this for a silent token re-sync on login. Returns null otherwise. Never throws.
 */
export async function getWebPushTokenIfGranted(): Promise<string | null> {
  try {
    if (getNotificationPermission() !== 'granted') return null;
    const m = await getMessagingIfSupported();
    if (!m) return null;
    const swReg = await registerServiceWorker();
    const token = await getToken(m, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg ?? undefined,
    });
    return token || null;
  } catch (e) {
    console.warn('[firebase] getWebPushTokenIfGranted failed:', e);
    return null;
  }
}

/**
 * Subscribe to foreground messages (tab focused). Background messages are handled
 * by the service worker. Returns an unsubscribe fn (or no-op if unsupported).
 */
export async function onForegroundMessage(
  handler: (payload: { title?: string; body?: string; data?: Record<string, any> }) => void
): Promise<() => void> {
  const m = await getMessagingIfSupported();
  if (!m) return () => {};
  return onMessage(m, (payload) => {
    // Backend sends data-only pushes to web — title/body ride in `data`.
    handler({
      title: payload.notification?.title ?? payload.data?.title,
      body: payload.notification?.body ?? payload.data?.body,
      data: payload.data,
    });
  });
}
